import type { ScopeSubjectId } from './authority/ny/scopeSubjects';
import type { AiObservationContent } from './aiObservation';
import type { V2ChecklistItem } from './inspection';
import { AI_GUARDED_TRADES, type ResponsibleTrade } from './responsibleTrade';
import { keywordSubjectOf } from './scopeGate';
import { splitSentences, stripProhibitedSentences } from './wordingGuard';

// THE INSPECTION RULES LIBRARY (Owner device test 2026-09-13: "什么东西根据规则应该怎么查、建议什么人来
// 检查，应该清楚归类放在库里，而不是让 AI 现场分析然后自由分配").
//
// The AI observes; this table decides. For a checklist row it names the professional and the app
// shows the row's own check method (the template hint) — the model's check steps, scope note and
// trade for that row are discarded. For a result that is not a row, the New York scope subject the
// gate resolved names the professional when the table knows it, otherwise the model's code stands.
// Everywhere, an instruction the inspection standard does not allow (moving an appliance, operating
// a valve, dismantling, entering an unsafe space) is removed sentence by sentence — the deterministic
// backstop behind the prompt rule.
//
// Trades are Owner-ratified data (docs/authority/ny/NY-OWNER-RULINGS-TRADES-WORDING-SCOPE-RATIFICATION-
// 20260913.md §3 / §4): the structure and foundation rows default to the mason / contractor — a Professional
// Engineer (PE) is reached only through the inspector-confirmed structural concern on the finding
// (referralForStructuralConcern), never by the row's name. The AI never earns a PE or a NYS Licensed Mold
// Assessor on its own (AI_GUARDED_TRADES). Changing a trade changes no test but the pin on that row.
export const INSPECTION_RULES_VERSION = 'inspection-rules-2026-09-15';

// Every checklist template row (src/domain/checklistTemplates.ts) → the professional the report
// names for a deficiency on it; null = the row is an observation / method row without a trade.
export const ROW_TRADES: Readonly<Record<string, ResponsibleTrade | null>> = Object.freeze({
  // Exterior
  'ext-wall-covering': 'qualifiedSidingContractor',
  'ext-foundation-visible': 'qualifiedMasonryContractor',
  'ext-grading': 'qualifiedDrainageGradingContractor',
  'ext-gutters': 'qualifiedRoofingContractor',
  'ext-windows-doors': 'qualifiedWindowDoorContractor',
  'ext-entry-steps': 'qualifiedGeneralContractor',
  'ext-deck-patio': 'qualifiedGeneralContractor',
  'ext-driveway': 'qualifiedPavingContractor',
  'ext-gfci': 'licensedElectrician',
  'ext-hose-bibb': 'licensedPlumbingProfessional',
  'ext-neighbor-influence': 'qualifiedDrainageGradingContractor',
  'ext-wdi': 'licensedPestProfessional',
  // Roof
  'roof-covering': 'qualifiedRoofingContractor',
  'roof-method': null,
  'roof-flashing': 'qualifiedRoofingContractor',
  'roof-valleys-edges': 'qualifiedRoofingContractor',
  'roof-chimney': 'qualifiedChimneyProfessional',
  'roof-ventilation': 'qualifiedRoofingContractor',
  'roof-skylight': 'qualifiedRoofingContractor',
  'roof-gutter-attachment': 'qualifiedRoofingContractor',
  // Garage
  'garage-door-reverse': 'qualifiedGarageDoorTechnician',
  'garage-separation': 'qualifiedGeneralContractor',
  'garage-floor': 'qualifiedPavingContractor',
  'garage-gfci': 'licensedElectrician',
  'garage-structure': 'qualifiedGeneralContractor',
  'garage-appliances': 'qualifiedHvacContractor',
  // Basement
  'bsmt-access-finish': null,
  'bsmt-foundation-walls': 'qualifiedMasonryContractor',
  'bsmt-moisture': 'qualifiedWaterproofingContractor',
  'bsmt-structure-alignment': 'qualifiedGeneralContractor',
  'bsmt-sump': 'licensedPlumbingProfessional',
  'bsmt-stairs': 'qualifiedGeneralContractor',
  'bsmt-gfci': 'licensedElectrician',
  'bsmt-pests': 'licensedPestProfessional',
  'bsmt-mold': 'licensedMoldAssessor',
  'bsmt-alarm': 'licensedElectrician',
  'bsmt-outside-review': 'qualifiedWaterproofingContractor',
  'bsmt-crawlspace': 'qualifiedGeneralContractor',
  'bsmt-foundation-insulation': 'qualifiedInsulationContractor',
  // Structure
  'str-main-beam': 'qualifiedGeneralContractor',
  'str-post-base': 'qualifiedGeneralContractor',
  'str-joists': 'qualifiedGeneralContractor',
  'str-connections': 'qualifiedGeneralContractor',
  'str-bearing-walls': 'qualifiedGeneralContractor',
  'str-exterior-signs': 'qualifiedGeneralContractor',
  'str-limitations': null,
  // Electrical
  'elec-service-entrance': 'licensedElectrician',
  'elec-panel-overview': 'licensedElectrician',
  'elec-panel-interior': 'licensedElectrician',
  'elec-panel-label': 'licensedElectrician',
  'elec-grounding': 'licensedElectrician',
  'elec-wiring-type': 'licensedElectrician',
  'elec-gfci-afci': 'licensedElectrician',
  'elec-outlets-sample': 'licensedElectrician',
  'elec-fixtures': 'licensedElectrician',
  // Plumbing
  'plb-main-shutoff': 'licensedPlumbingProfessional',
  'plb-supply-piping': 'licensedPlumbingProfessional',
  'plb-drain-piping': 'licensedPlumbingProfessional',
  'plb-water-pressure': 'licensedPlumbingProfessional',
  'plb-fixtures-sample': 'licensedPlumbingProfessional',
  'plb-water-meter': 'licensedPlumbingProfessional',
  'plb-sewer-cleanout': 'licensedPlumbingProfessional',
  'plb-cross-connection': 'licensedPlumbingProfessional',
  // Water heater
  'wh-overview': 'licensedPlumbingProfessional',
  'wh-data-plate': null,
  'wh-tpr': 'licensedPlumbingProfessional',
  'wh-venting': 'licensedPlumbingProfessional',
  'wh-combustion-air': 'licensedPlumbingProfessional',
  'wh-connections': 'licensedPlumbingProfessional',
  'wh-base': 'licensedPlumbingProfessional',
  'wh-temperature': 'licensedPlumbingProfessional',
  // Heating / cooling
  'hvac-heating-unit': 'qualifiedHvacContractor',
  'hvac-operation': 'qualifiedHvacContractor',
  'hvac-venting': 'qualifiedHvacContractor',
  'hvac-filter': 'qualifiedHvacContractor',
  'hvac-clearance': 'qualifiedHvacContractor',
  'hvac-condensate': 'qualifiedHvacContractor',
  'hvac-distribution': 'qualifiedHvacContractor',
  'hvac-ac-unit': 'qualifiedHvacContractor',
  'hvac-refrigerant': 'qualifiedHvacContractor',
  'hvac-boiler-relief': 'qualifiedHvacContractor',
  'hvac-oil-tank': 'qualifiedHvacContractor',
  // 2026-10-28: two CONDITIONAL rows (checklistTemplates.ts CONDITIONAL_TEMPLATE_ROWS) had no entry, so a defect on
  // them named no professional at all; the fixed supplementary heaters are heating equipment.
  'hvac-supplementary-heat': 'qualifiedHvacContractor',
  // Attic
  'attic-access': null,
  'attic-sheathing': 'qualifiedRoofingContractor',
  'attic-insulation': 'qualifiedInsulationContractor',
  'attic-ventilation': 'qualifiedRoofingContractor',
  'attic-exhaust-termination': 'qualifiedHvacContractor',
  'attic-structure': 'qualifiedGeneralContractor',
  'attic-wiring': 'licensedElectrician',
  'attic-pests': 'licensedPestProfessional',
  // Interior rooms
  'int-limitations': null,
  'int-surfaces': 'qualifiedGeneralContractor',
  'int-windows': 'qualifiedWindowDoorContractor',
  'int-doors': 'qualifiedGeneralContractor',
  'int-outlets': 'licensedElectrician',
  'int-stairs': 'qualifiedGeneralContractor',
  'int-fireplace': 'qualifiedChimneyProfessional',
  'int-alarms': 'licensedElectrician',
  // Kitchen
  'kit-sink': 'licensedPlumbingProfessional',
  'kit-gfci': 'licensedElectrician',
  'kit-exhaust-ducting': 'qualifiedHvacContractor',
  'kit-cabinets-counters': 'qualifiedGeneralContractor',
  'kit-gas': 'licensedPlumbingProfessional',
  'kit-surfaces': 'qualifiedGeneralContractor',
  // Bathroom
  'bath-tub-shower': 'qualifiedGeneralContractor',
  'bath-toilet': 'licensedPlumbingProfessional',
  'bath-sink': 'licensedPlumbingProfessional',
  'bath-exhaust': 'qualifiedHvacContractor',
  'bath-gfci': 'licensedElectrician',
  'bath-surfaces': 'qualifiedGeneralContractor',
  'bath-water-flow': 'licensedPlumbingProfessional',
  // Bedroom
  'bed-egress': 'qualifiedWindowDoorContractor',
  'bed-windows-doors': 'qualifiedWindowDoorContractor',
  'bed-outlets': 'licensedElectrician',
  'bed-surfaces': 'qualifiedGeneralContractor',
  'bed-alarm': 'licensedElectrician',
  'bed-closet': 'licensedElectrician',
  // Safety review
  'safe-alarms-system': 'licensedElectrician',
  'safe-handrails': 'qualifiedGeneralContractor',
  'safe-garage-door': 'qualifiedGarageDoorTechnician',
  'safe-tpr': 'licensedPlumbingProfessional',
  'safe-egress': 'qualifiedWindowDoorContractor',
  'safe-electrical-hazards': 'licensedElectrician',
  'safe-trip-fall': 'qualifiedGeneralContractor',
  'safe-wdi': 'licensedPestProfessional',
  'other-condition': null
});

// A result that is not a checklist row: the New York scope subject the gate resolved → the trade.
export const SUBJECT_TRADES: Readonly<Partial<Record<ScopeSubjectId, ResponsibleTrade>>> = Object.freeze({
  site: 'qualifiedDrainageGradingContractor',
  structure: 'qualifiedGeneralContractor',
  foundation: 'qualifiedMasonryContractor',
  'exterior-cladding': 'qualifiedSidingContractor',
  'windows-doors': 'qualifiedWindowDoorContractor',
  'stairs-railings': 'qualifiedGeneralContractor',
  'attached-structures': 'qualifiedGeneralContractor',
  roof: 'qualifiedRoofingContractor',
  'roof-drainage': 'qualifiedRoofingContractor',
  chimney: 'qualifiedChimneyProfessional',
  fireplace: 'qualifiedChimneyProfessional',
  attic: 'qualifiedGeneralContractor',
  'insulation-ventilation': 'qualifiedInsulationContractor',
  basement: 'qualifiedWaterproofingContractor',
  crawlspace: 'qualifiedGeneralContractor',
  'sump-pump': 'licensedPlumbingProfessional',
  plumbing: 'licensedPlumbingProfessional',
  'water-heater': 'licensedPlumbingProfessional',
  'fuel-storage': 'qualifiedHvacContractor',
  electrical: 'licensedElectrician',
  'smoke-co-detectors': 'licensedElectrician',
  heating: 'qualifiedHvacContractor',
  cooling: 'qualifiedHvacContractor',
  interior: 'qualifiedGeneralContractor',
  garage: 'qualifiedGeneralContractor',
  'household-appliances': 'qualifiedServiceTechnician',
  'window-wall-ac': 'qualifiedHvacContractor',
  'pest-wdi': 'licensedPestProfessional'
  // mold: no AI-side trade — the NYS Licensed Mold Assessor is reached only through the inspector's own defect on the mold row.
});

// undefined = the row is not in the table (an inspector-added row, a retired row): no rule.
export function tradeForTemplateRow(templateId: string | null): ResponsibleTrade | null | undefined {
  if (templateId === null) return undefined;
  return Object.prototype.hasOwnProperty.call(ROW_TRADES, templateId) ? ROW_TRADES[templateId] : undefined;
}

// Ratification 2026-09-13 §4: REFERRAL = ROW + INSPECTOR-CONFIRMED FACT + REFERRAL AUTHORITY. The row's
// default (mason / contractor) stands until the inspector confirms movement, displacement, significant
// cracking, deformation, suspected instability or a load-bearing concern on the finding.
export function referralForStructuralConcern(base: ResponsibleTrade | null, structuralConcern: boolean): ResponsibleTrade | null {
  return structuralConcern ? 'structuralEngineer' : base;
}

export function tradeForSubject(subjectId: ScopeSubjectId | null): ResponsibleTrade | null {
  return subjectId === null ? null : (SUBJECT_TRADES[subjectId] ?? null);
}

// 2026-10-28 (iPhone real-photo run 2026-10-26, #8: rust on the oil tank's fittings went to a WATERPROOFING
// contractor). Two corrections to the professional of a result that is not a checklist row:
//   - a subject that only says WHERE the thing is — the basement, the attic, the garage, the crawl space — does
//     not name the professional when the text names the thing itself ("the oil tank fittings in the basement"
//     resolved to the basement, whose trade is the waterproofing contractor's); the thing's subject does;
//   - fuel storage covers oil and gas alike: fuel-oil equipment (the tank, its lines, the filter, the burner
//     feed) is the oil-burner / HVAC contractor's, gas piping, the meter and the gas shut-off are the plumbing
//     professional's — the same as the kitchen gas row (pinned 2026-09-13: a gas matter is a plumbing matter).
// The gate's subject and its decision are untouched; only the professional changes.
export const LOCATION_SUBJECTS: readonly ScopeSubjectId[] = Object.freeze(['basement', 'attic', 'garage', 'crawlspace']);

const FUEL_OIL_WORDS = /\b(oil|fuel[\s-]oil|heating oil|kerosene|propane|lp tank|oil burner)\b|油罐|燃油|油箱|油管|油路|丙烷|滤油/i;
const GAS_WORDS = /\b(gas|regulator)\b|燃气|煤气|天然气/i;
// Fuel-oil EQUIPMENT named in words the subject keywords do not list ("燃油管线", "oil supply line").
const FUEL_OIL_EQUIPMENT = /\b(oil tank|fuel tank|fuel[\s-]oil|heating oil|oil (?:line|lines|piping|pipe|pipes|supply|filter|feed|burner)|fuel (?:line|lines|piping))\b|油罐|燃油|油箱|油管|油路|滤油/i;

function ruleText(content: Pick<AiObservationContent, 'title' | 'headline' | 'observation' | 'observationUi' | 'recommendation' | 'question'>): string {
  return [content.title, content.headline, content.observation, content.observationUi, content.recommendation, content.question].filter((s): s is string => typeof s === 'string' && s.length > 0).join('\n');
}

export function tradeForResult(content: AiObservationContent, subjectId: ScopeSubjectId | null): ResponsibleTrade | null {
  const text = ruleText(content);
  const located = subjectId !== null && LOCATION_SUBJECTS.includes(subjectId);
  const subject = located ? (keywordSubjectOf(content, LOCATION_SUBJECTS) ?? (FUEL_OIL_EQUIPMENT.test(text) ? 'fuel-storage' : subjectId)) : subjectId;
  if (subject === 'fuel-storage') return GAS_WORDS.test(text) && !FUEL_OIL_WORDS.test(text) ? 'licensedPlumbingProfessional' : 'qualifiedHvacContractor';
  return tradeForSubject(subject);
}

// Instructions the standard of practice does not allow the inspector to follow (visual, non-invasive;
// NY 197-5: no moving of furniture, appliances or stored items, no dismantling, no operation of
// safety or shut-off devices). Matched sentence by sentence; a match removes that sentence only.
export const INVASIVE_INSTRUCTION_PATTERNS: readonly RegExp[] = Object.freeze([
  /(移动|挪动|挪开|搬动|搬开|移开|拉出|拉开|抽出|拖出|移走)[^，。；\n]{0,8}(灶|炉|设备|家电|电器|冰箱|洗衣机|烘干机|洗碗机|家具|物品|杂物|柜|机|热水器|储物)/,
  /拆(卸|开|下|除|掉)/,
  /(掀|揭|翻|卷)开?[^，。；\n]{0,6}(地毯|保温|绝缘|地垫|盖板|面板|吊顶板)/,
  /(关闭|操作|开关|扳动|旋转|试关|试开|开启|测试)[^，。；\n]{0,10}(关断阀|截止阀|总阀|燃气阀|煤气阀|泄压阀|安全阀|阀门|阀)/,
  /(切断|断开|拉下|合上)[^，。；\n]{0,8}(燃气|煤气|总闸|主开关|总开关|电源总|断路器)/,
  /(进入|爬进|钻进|爬入)[^，。；\n]{0,8}(狭小|受限|不安全|爬行|夹层)/,
  /(查看|检查|观察|确认|拍摄|拍)[^，。；\n]{0,8}(灶具|灶|炉具|冰箱|洗衣机|烘干机|洗碗机|电器|家电|设备|热水器)[^，。；\n]{0,3}(后方|背后|后面|背面|后部)/,
  /\b(inspect|check|look|view|access|photograph|verify)\b[^.;\n]{0,24}\bbehind (the|this|that)\b[^.;\n]{0,16}\b(range|stove|oven|cooktop|appliance|refrigerator|fridge|washer|dryer|dishwasher|water heater|unit)\b/i,
  /\b(move|moving|pull out|pull the|pulling|slide out|relocate|shift|remove|removing|lift|lifting)\b[^.;\n]{0,40}\b(range|stove|oven|cooktop|appliance|refrigerator|fridge|washer|dryer|dishwasher|water heater|furniture|stored items|belongings|storage|boxes|cabinet)\b/i,
  /\b(dismantle|disassemble|take apart)\b/i,
  /\b(lift|pull back|peel back|roll back)\b[^.;\n]{0,20}\b(carpet|rug|insulation|flooring|ceiling tile)\b/i,
  /\b(operate|operating|close|closing|shut|turn|open|opening|cycle|test|testing|actuate)\b[^.;\n]{0,24}\b(shut-?off valve|gas valve|main valve|supply valve|relief valve|tpr valve|t&p valve|valve)\b/i,
  /\b(shut off|turn off|switch off|disconnect|reset)\b[^.;\n]{0,20}\b(gas|main breaker|main disconnect|service disconnect|power to the)\b/i,
  /\b(enter|crawl into|climb into)\b[^.;\n]{0,24}\b(confined|unsafe|crawl ?space|attic space)\b/i
]);

export function isInvasiveInstruction(sentence: string): boolean {
  return INVASIVE_INSTRUCTION_PATTERNS.some((pattern) => pattern.test(sentence));
}

// Sentence-level removal, never a paragraph deleter: the offending sentence goes, the rest stays.
export function stripInvasiveInstructions(text: string): string {
  if (text.trim().length === 0) return text;
  const kept = splitSentences(text).filter((sentence) => !isInvasiveInstruction(sentence));
  return kept.join('').trim();
}

export function filterInvasiveSteps(steps: readonly string[]): string[] {
  return steps.filter((step) => !isInvasiveInstruction(step));
}

// The rules applied to one AI result, after the Scope Gate and before it becomes a card. Pure.
export function applyInspectionRules(content: AiObservationContent, items: readonly V2ChecklistItem[], subjectId: ScopeSubjectId | null): AiObservationContent {
  const item = content.type === 'existingItem' && content.itemId ? (items.find((i) => i.id === content.itemId) ?? null) : null;
  const rowTrade = item ? tradeForTemplateRow(item.templateId) : undefined;
  const next: AiObservationContent = { ...content, checkSteps: [...content.checkSteps] };
  if (rowTrade !== undefined) {
    // A checklist row: the library names the professional and the row's own hint says how to check.
    next.responsibleTrade = rowTrade;
    next.checkSteps = [];
    next.scopeNote = null;
  } else {
    const subjectTrade = tradeForResult(content, subjectId);
    if (subjectTrade !== null) next.responsibleTrade = subjectTrade;
    next.checkSteps = filterInvasiveSteps(content.checkSteps);
    next.scopeNote = content.scopeNote ? stripInvasiveInstructions(content.scopeNote) || null : null;
  }
  // Ratification 2026-09-13 §3 / §4 / §7: a Professional Engineer or a NYS Licensed Mold Assessor is never
  // named from an AI observation alone — the inspector confirms the structural / mold-related concern.
  if (next.responsibleTrade !== null && AI_GUARDED_TRADES.includes(next.responsibleTrade)) {
    next.responsibleTrade = next.responsibleTrade === 'structuralEngineer' ? 'qualifiedGeneralContractor' : null;
  }
  next.guidance = stripInvasiveInstructions(content.guidance);
  next.question = content.question ? stripInvasiveInstructions(content.question) || null : null;
  // The screen-language mirror of the report sentences is guarded like the sentences it mirrors.
  if (content.observationUi) next.observationUi = stripProhibitedSentences(content.observationUi, 'report').text || null;
  if (content.recommendationUi) next.recommendationUi = stripProhibitedSentences(content.recommendationUi, 'report').text || null;
  return next;
}

// Ratification 2026-09-13 §4: the rows on which the inspector may confirm the STRUCTURAL FACT that turns
// the row's referral into a Professional Engineer (PE). Only these rows offer the confirmation (Final
// Owner Decision 2026-09-14 §15.6: a small control on the row, never a large card).
export const STRUCTURAL_FACT_ROWS: readonly string[] = Object.freeze([
  'ext-foundation-visible',
  'bsmt-foundation-walls',
  'bsmt-structure-alignment',
  'str-main-beam',
  'str-post-base',
  'str-joists',
  'str-connections',
  'str-bearing-walls',
  'str-exterior-signs',
  'attic-structure',
  'garage-structure'
]);

export function rowCanConfirmStructuralConcern(templateId: string | null): boolean {
  return templateId !== null && STRUCTURAL_FACT_ROWS.includes(templateId);
}
